// Session state. Capabilities here drive navigation only; every action is re-authorized by the API.
// The query cache is partitioned by organisation + user and cleared whenever the session changes.
import type { SessionResponse } from '@passage/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { ApiRequestError, api, get, setCsrfToken, setRequestLocale } from './api.ts';
import { useI18n } from './i18n.tsx';

export interface SessionValue {
  readonly session: SessionResponse;
  /** Cache-key prefix that partitions all queries by organisation and user. */
  readonly scopeKey: readonly [string, string];
  can(action: string, siteId?: string | null): boolean;
  canAnywhere(action: string): boolean;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

export const SESSION_QUERY_KEY = ['session'] as const;
/** Queries that are not organisation/user data and survive session changes. */
const GLOBAL_KEYS = new Set(['session', 'auth-context']);

export function useSessionQuery() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async () => {
      try {
        const s = await get<SessionResponse>('/api/v1/session');
        setCsrfToken(s.csrfToken);
        return s;
      } catch (e) {
        if (e instanceof ApiRequestError && e.status === 401) {
          setCsrfToken(null);
          return null;
        }
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function SessionProvider({ session, children }: { session: SessionResponse; children: ReactNode }) {
  const qc = useQueryClient();
  const { adopt, setPersist, setDisplay, locale } = useI18n();
  // PD-011: the account's stored language wins at sign-in; later changes are saved to the account.
  useEffect(() => {
    adopt(session.user.locale);
  }, [adopt, session.user.id, session.user.locale]);
  // OR-10 (PD-056): organisation date style and clock.
  const { dateStyle, hourCycle } = session.display ?? { dateStyle: 'medium', hourCycle: 'locale' };
  useEffect(() => {
    setDisplay({ dateStyle, hourCycle });
  }, [setDisplay, dateStyle, hourCycle]);
  useEffect(() => {
    setPersist((l) => void api<SessionResponse>('PATCH', '/api/v1/me/preferences', { locale: l }).then((s) => qc.setQueryData(SESSION_QUERY_KEY, s)));
    return () => setPersist(null);
  }, [qc, setPersist]);
  const scopeKey = useMemo(() => [session.organisation.id, session.user.id] as const, [session.organisation.id, session.user.id]);

  // A change of user, organisation or access version invalidates every cached record.
  useEffect(() => {
    qc.removeQueries({ predicate: (q) => !GLOBAL_KEYS.has(String(q.queryKey[0])) && (q.queryKey[0] !== scopeKey[0] || q.queryKey[1] !== scopeKey[1]) });
  }, [qc, scopeKey]);
  useEffect(() => {
    void qc.invalidateQueries({ predicate: (q) => !GLOBAL_KEYS.has(String(q.queryKey[0])) });
  }, [qc, session.accessVersion]);
  // PD-015: labels and messages come localised from the API, so a language change refetches data.
  const firstLocale = useRef(locale);
  useEffect(() => {
    if (firstLocale.current === locale) return;
    firstLocale.current = locale;
    // Child effects run before the I18nProvider's, so set the request language here first.
    setRequestLocale(locale);
    void qc.invalidateQueries({ predicate: (q) => !GLOBAL_KEYS.has(String(q.queryKey[0])) });
  }, [qc, locale]);

  const can = useCallback(
    (action: string, siteId?: string | null) => session.access.tenant.includes(action) || (siteId ? (session.access.sites[siteId] ?? []).includes(action) : false),
    [session],
  );
  const canAnywhere = useCallback(
    (action: string) => session.access.tenant.includes(action) || Object.values(session.access.sites).some((a) => a.includes(action)),
    [session],
  );
  const signOut = useCallback(async () => {
    try {
      await api('POST', '/api/v1/auth/logout');
    } finally {
      setCsrfToken(null);
      qc.clear();
      qc.setQueryData(SESSION_QUERY_KEY, null);
    }
  }, [qc]);
  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
  }, [qc]);

  const value = useMemo(() => ({ session, scopeKey, can, canAnywhere, signOut, refresh }), [session, scopeKey, can, canAnywhere, signOut, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
